import React, { useCallback, useEffect, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { useDetectClickOutside } from 'react-detect-click-outside'
import useTranslation from 'next-translate/useTranslation'
import GInput from 'tennders-common/form/components/input'
import GLoading from 'tennders-market/common/components/element/GLoading'
import { SelectType } from 'tennders-common/types/SelectType'
import { LocationParamsType } from 'tennders-common/types/LocationParams'
import useCountries from 'tennders-common/hooks/useCountries'
import DOMPurify from 'dompurify'
import useInitials from 'tennders-common/hooks/useInitials'

const center = { lat: 50.012100, lng: 20.985842 }

// Create a bounding box with sides ~10km away from the center point
const defaultBounds = {
  north: center.lat + 10,
  south: center.lat - 10,
  east: center.lng + 10,
  west: center.lng - 10
}

type Props = Omit< React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onChange'> & {
    type?: string,
    form?: Record<string, any>,
    name?: string,
    onChange?: (name: string, value: LocationParamsType) => void,
    placeholder?: string,
    onlyInput?: boolean
    withDiv?: boolean,
    relative?: boolean,
    onSet?: (value: LocationParamsType) => void,
    value?: string,
    error?: string,
    errors?: Record<string, any>,
    top?: boolean | string | number,
    onTextSet?: (value: string) => void,
    labelClass?: string,
    children?: React.ReactNode | React.ReactNode[],
    eClass?: string,
    label?: string,
    inputClass?: string,
    labelKey?: string,
    smart?: boolean,
    url?: string,
    items?: SelectType[],
    google?: boolean,
    readOnly?: boolean,
    withChildren?: boolean,
    noDiv?: boolean,
    disabled?: boolean,
    asInput ?: boolean,
    onTextChanged?: (name: string, text: string) => void,
    keyPrefix ?: string
}

const GLocation = ({
  onChange, onSet, name, error, placeholder, label, withDiv, value, eClass,
  onlyInput, inputClass, withChildren, noDiv, disabled,
  onTextChanged: onTextChangedCallback,
  labelClass, smart, asInput,
  google, readOnly, keyPrefix, ...rest
}: Props) => {
  const { t } = useTranslation()
  const { boxOwner } = useInitials()
  const { supportedCounties } = useCountries()
  const [suggestions, setsuggestions] = useState([])
  const [text, settext] = useState<string | boolean>('')
  const [isSmart, setIsSmart] = useState(smart)
  const [smartLocations, setSmartLocations] = useState([])

  const closeDropdown = useCallback(() => {
    setsuggestions([])
  }, [])
  const ref = useDetectClickOutside({ onTriggered: closeDropdown })

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading
  } = usePlacesService({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
    language: 'en',
    options: {
      types: ['(regions)'],
      bounds: defaultBounds,
      input: undefined
    }
  })

  const searchGoogle = (value) => {
    if (google && value.length > 0) {
      getPlacePredictions({ input: value })
    } else if (value.length > 0) {
      fetch(
        `https://autocomplete.geocoder.ls.hereapi.com/6.2/suggest.json?apikey=${process.env.NEXT_PUBLIC_HEREMAP_API_KEY}
        &language=en&lang=en-US&country=${process.env.NEXT_PUBLIC_HERE_COUNTRIES}
        &beginHighlight=<b>&endHighlight=</b>&query=${value}`,
        { headers: { 'Accept-Language': 'en-US' } }
      )
        .then((response) => response.json())
        .then((data) => {
          const op = data.suggestions.map((o) => ({
            label: o.label,
            locationId: o.locationId
          }))
          setsuggestions(op)
        })
        .catch(() => {
          setsuggestions([])
        })
    } else {
      setsuggestions([])
    }
  }

  const onTextChanged = (value) => {
    settext(value)
    setIsSmart(smart)
    setsuggestions([])
    if (onTextChangedCallback) {
      onTextChangedCallback(name, value as string)
    }
    if (smart) {
      const locations = smartLocations?.filter(loc => (!asInput || loc.id) && loc?.label.trim().startsWith(value.toLocaleUpperCase(navigator.language).trim()))
      if (locations.length === 0) {
        setIsSmart(false)
        searchGoogle(value)
      } else {
        setsuggestions(locations.length > 10 ? locations.slice(0, 9) : locations)
      }
    } else {
      searchGoogle(value)
    }
  }

  const smartSuggestionSelected = (item) => {
    const location = { ...item, smart: true }
    if (onChange && name) {
      onChange(name, location)
    } else if (onSet) {
      onSet(location)
    }
    setsuggestions([])
  }

  const suggestionSelected = (item) => {
    placesService?.getDetails(
      { placeId: item.place_id },
      (place) => {
        const addr = [...place.address_components]
        const locationParams : LocationParamsType = {
          id: place.place_id,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          label: place.formatted_address
        }
        while (addr.length > 0) {
          const addrItem = addr.pop()
          if (addrItem.types.indexOf('country') > -1) {
            locationParams.country = addrItem.short_name
          } else if (addrItem.types.indexOf('state') > -1) {
            locationParams.state = addrItem.long_name
          } else if (addrItem.types.indexOf('locality') > -1) {
            locationParams.city = addrItem.long_name
          } else if (addrItem.types.indexOf('administrative_area_level_1') > -1) {
            if (!locationParams.city) {
              locationParams.city = addrItem.long_name
            }
          } else if (addrItem.types.indexOf('postal_code') > -1) {
            locationParams.postalCode = addrItem.long_name
          }
        }
        if (!locationParams.country) {
          window.alert(t('prompt:no_country_detected'))
          return
        }

        if (onChange && name) {
          onChange(name, locationParams)
        } else if (onSet) {
          onSet(locationParams)
        }
        setsuggestions([])
      }
    )
  }

  const renderSuggestions = () => {
    if (isPlacePredictionsLoading) {
      return (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%'
        }}>
          <GLoading color="#000" size={14} />
        </div>
      )
    }
    if (suggestions.length === 0) {
      return null
    }
    return (
      <ul
        ref={ref}
        style={{
          position: 'absolute',
          top: '70px',
          left: '0',
          padding: 12,
          border: '1px solid #ddd',
          boxShadow: '0 0 10px rgb(0 0 0 / 15%)',
          backgroundColor: 'white',
          zIndex: 99999
        }}
      >
        {suggestions?.map((item) => (
          <li
            style={{ cursor: 'pointer' }}
            key={keyPrefix + item.locationId || item.place_id}
            onClick={() => suggestionSelected(item)}
          >
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.label || item.description) }} />
          </li>
        ))}
      </ul>
    )
  }

  const renderSmartSuggestions = () => {
    if (isPlacePredictionsLoading) {
      return (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%'
        }}>
          <GLoading color="#000" size={14} />
        </div>
      )
    }
    if (suggestions.length === 0) {
      return null
    }
    return (
      <ul
        ref={ref}
        style={{
          position: 'absolute',
          top: '70px',
          left: '0',
          padding: 12,
          width: 300,
          border: '1px solid #ddd',
          boxShadow: '0 0 10px rgb(0 0 0 / 15%)',
          backgroundColor: 'white',
          zIndex: 99999
        }}
      >
        {suggestions?.map((item) => (
          <li
            style={{ cursor: 'pointer' }}
            key={keyPrefix + item.id}
            onClick={() => smartSuggestionSelected(item)}
          >
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item?.label) }} />
          </li>
        ))}
      </ul>
    )
  }

  useEffect(() => {
    // fetch place details for the first element in placePredictions array
    if (placePredictions.length > 0) {
      setsuggestions(placePredictions)
    }
  }, [placePredictions])

  useEffect(() => {
    settext(value || '')
  }, [value])

  return (
    <div className="pos-rel" {...rest}>
      <>
        <GInput
          disabled={disabled}
          onlyInput={onlyInput}
          labelClass={labelClass}
          className={inputClass}
          readOnly={readOnly}
          error={error}
          eClass={eClass || 'mr-8'}
          val={(text || ' ') as string}
          onSet={onTextChanged}
          placeholder={placeholder}
          label={label}
          withDiv={!noDiv}
          noFieldSet={noDiv}
        />
        {isSmart ? renderSmartSuggestions() : renderSuggestions()}
        {withChildren}
      </>

    </div>
  )
}

export default GLocation
